import { AppLink } from "@/components/common/app-link";
import SectionPage from "@/components/common/section-page";
import AwesomeCard from "@/components/common/awesome-card";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { BeachDestinationDetail } from "@/lib/data/beach-destination-details";
import { getRelatedBeachDestinations } from "@/lib/data/beach-destination-details";
import { THE_PLACE_TO_BE_TITLE } from "@/lib/data/the-place-to-be";
import BeachPracticalInfo from "@/components/site-officiel/ivoirexperience/beach-practical-info";
import ImageGallery from "@/components/common/image-gallery";
import { ChevronRight } from "lucide-react";

function toBackgroundImageUrl(src: string) {
    const encoded = src.split("/").map((segment) => encodeURIComponent(segment)).join("/");
    return `url("${encoded}")`;
}

export default function BeachDestinationDetailView({ destination }: { destination: BeachDestinationDetail }) {
    const relatedDestinations = getRelatedBeachDestinations(destination.slug);
    const hasHighlights = Boolean(destination.highlightBeaches?.length);
    const hasActivities = Boolean(destination.activities?.length);

    return (
        <div className="flex flex-col gap-6 pb-6 bg-slate-100">
            <div className="relative min-h-[380px] flex flex-col justify-center items-center text-center px-4 py-16 bg-slate-700">
                <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: toBackgroundImageUrl(destination.heroImage) }}
                />
                <div className="absolute inset-0 bg-black/45" />
                <div className="relative z-10 max-w-3xl">
                    <h1 className="text-white font-exo-bold text-4xl md:text-5xl mb-3">{destination.name}</h1>
                    <p className="text-white/90 text-lg mb-6">{destination.subtitle}</p>
                    <div className="flex flex-wrap justify-center gap-2">
                        {destination.heroTags.map((tag) => (
                            <span
                                key={tag.label}
                                className="inline-flex items-center gap-1 rounded-full bg-white/20 backdrop-blur-sm px-4 py-1.5 text-sm text-white border border-white/30"
                            >
                                <span aria-hidden>{tag.icon}</span>
                                {tag.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            <SectionPage sectionType="2xl">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/ivoirexperience/the-place-to-be">Ivoire Experience</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/ivoirexperience/the-place-to-be">{THE_PLACE_TO_BE_TITLE}</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>{destination.name}</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </SectionPage>

            <SectionPage sectionType="2xl">
                <AwesomeCard className="bg-white text-sm leading-relaxed text-slate-600" titleClassName="text-bleu-marine">
                    <h2 className="text-2xl font-exo-bold text-bleu-marine mb-4">{destination.welcomeTitle}</h2>
                    <div className="space-y-4">
                        {destination.welcomeParagraphs.map((paragraph) => (
                            <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                        ))}
                        {destination.closingParagraph && <p>{destination.closingParagraph}</p>}
                    </div>
                </AwesomeCard>
            </SectionPage>

            <SectionPage sectionType="2xl">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-exo-bold text-bleu-marine mb-2">Galerie photos</h2>
                    <p className="text-lg text-bleu-ciel">
                        Plongez dans l&apos;ambiance de {destination.name}
                    </p>
                </div>
                <ImageGallery
                    images={destination.galleryImages.map((src, index) => ({
                        src,
                        alt: `${destination.name} - photo ${index + 1}`,
                    }))}
                    gridClassName="grid grid-cols-1 md:grid-cols-3 gap-4"
                    imageClassName="w-full aspect-[4/3] object-cover rounded-xl hover:opacity-90 transition-opacity cursor-pointer"
                    aspectRatio="auto"
                />
            </SectionPage>

            {hasHighlights && (
                <SectionPage sectionType="2xl">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-exo-bold text-bleu-marine mb-2">
                            Les plages à ne pas manquer
                        </h2>
                        <p className="text-lg text-bleu-ciel">
                            Explorez les plus beaux spots d&apos;{destination.name}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {destination.highlightBeaches!.map((beach) => (
                            <div
                                key={beach.title}
                                className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
                            >
                                <h3 className="text-lg font-exo-bold text-bleu-marine mb-2">{beach.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{beach.description}</p>
                            </div>
                        ))}
                    </div>
                </SectionPage>
            )}

            {hasActivities && (
                <SectionPage sectionType="2xl">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-exo-bold text-bleu-marine mb-2">
                            {destination.activitiesTitle ?? "Activités et loisirs"}
                        </h2>
                        <p className="text-lg text-bleu-ciel">
                            {destination.activitiesSubtitle ?? "Des sensations fortes dans un cadre préservé"}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {destination.activities!.map((activity) => (
                            <div
                                key={activity.title}
                                className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 text-center"
                            >
                                <div className="text-4xl mb-4" aria-hidden>{activity.icon}</div>
                                <h3 className="text-lg font-exo-bold text-bleu-marine mb-2">{activity.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{activity.description}</p>
                            </div>
                        ))}
                    </div>
                </SectionPage>
            )}

            {destination.practicalInformation && (
                <SectionPage sectionType="2xl">
                    <BeachPracticalInfo information={destination.practicalInformation} />
                </SectionPage>
            )}

            {destination.safetyGuidelines && (
                <SectionPage sectionType="2xl">
                    <AwesomeCard
                        title="Recommandations"
                        className="bg-white text-sm leading-relaxed text-slate-600"
                        titleClassName="text-bleu-marine"
                    >
                        <ul className="space-y-2">
                            {destination.safetyGuidelines.map((guideline) => (
                                <li key={guideline} className="flex items-start gap-2 text-sm leading-relaxed">
                                    <span className="text-bleu-ciel mt-0.5">•</span>
                                    <span>{guideline}</span>
                                </li>
                            ))}
                        </ul>
                    </AwesomeCard>
                </SectionPage>
            )}

            <SectionPage sectionType="2xl">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-exo-bold text-bleu-marine mb-2">
                        Autres destinations à découvrir
                    </h2>
                    <p className="text-lg text-bleu-ciel">
                        Prolongez votre voyage sur le littoral ivoirien
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedDestinations.map((related) => (
                        <AppLink
                            key={related.slug}
                            to={`/ivoirexperience/the-place-to-be/${related.slug}`}
                            className="group relative block aspect-[16/10] overflow-hidden rounded-xl"
                        >
                            <img
                                src={related.heroImage}
                                alt={related.name}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-5 flex items-center justify-between">
                                <span className="text-white text-xl font-exo-bold">{related.name}</span>
                                <ChevronRight className="h-5 w-5 text-white" />
                            </div>
                        </AppLink>
                    ))}
                </div>
            </SectionPage>
        </div>
    );
}
